import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Building, Mail, Palette, Shield, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSettings(data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (res.ok) toast.success('Paramètres sauvegardés');
      else toast.error('Erreur lors de la sauvegarde');
    } catch (e) { toast.error('Erreur de connexion'); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  if (loading || !settings) return (
    <Layout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div></Layout>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="settings-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Settings className="h-6 w-6 text-teal-500" />
              Paramètres
            </h1>
            <p className="text-slate-500 mt-1">Configurez votre plateforme de formation</p>
          </div>
          <Button className="bg-teal-500 hover:bg-teal-600 text-white" onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>

        <Tabs defaultValue="organisme">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="organisme"><Building className="h-4 w-4 mr-1" />Organisme</TabsTrigger>
            <TabsTrigger value="email"><Mail className="h-4 w-4 mr-1" />Email</TabsTrigger>
            <TabsTrigger value="theme"><Palette className="h-4 w-4 mr-1" />Thème</TabsTrigger>
            <TabsTrigger value="securite"><Shield className="h-4 w-4 mr-1" />Sécurité</TabsTrigger>
          </TabsList>

          <TabsContent value="organisme" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Informations de l'organisme</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Nom de l'organisme</Label><Input value={settings.organisme_name} onChange={e => updateField('organisme_name', e.target.value)} data-testid="org-name" /></div>
                  <div><Label>Nom complet</Label><Input value={settings.organisme_full} onChange={e => updateField('organisme_full', e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Email de contact</Label><Input value={settings.email_contact} onChange={e => updateField('email_contact', e.target.value)} /></div>
                  <div><Label>Téléphone</Label><Input value={settings.phone} onChange={e => updateField('phone', e.target.value)} /></div>
                </div>
                <div><Label>Adresse</Label><Input value={settings.address} onChange={e => updateField('address', e.target.value)} placeholder="Adresse postale" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>N° SIRET</Label><Input value={settings.siret} onChange={e => updateField('siret', e.target.value)} placeholder="XXX XXX XXX XXXXX" /></div>
                  <div><Label>N° Déclaration d'activité (NDA)</Label><Input value={settings.nda} onChange={e => updateField('nda', e.target.value)} placeholder="XXXXXXXXXXX" /></div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-teal-50 rounded-lg">
                  <Switch checked={settings.qualiopi} onCheckedChange={v => updateField('qualiopi', v)} />
                  <div>
                    <p className="font-medium text-slate-900">Certification Qualiopi</p>
                    <p className="text-sm text-slate-500">Organisme certifié Qualiopi</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="email" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Configuration email</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <div>
                    <p className="font-medium text-emerald-800">SendGrid connecté</p>
                    <p className="text-sm text-emerald-600">Les emails transactionnels sont actifs</p>
                  </div>
                </div>
                <div><Label>Email d'expédition</Label><Input value="no-reply@affea.fr" disabled className="bg-slate-50" /></div>
                <div><Label>Nom d'expéditeur</Label><Input value={settings.organisme_name} disabled className="bg-slate-50" /></div>
                <p className="text-sm text-slate-500">Les certificats et notifications sont envoyés automatiquement via SendGrid.</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="theme" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Personnalisation visuelle</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Couleur principale</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-10 h-10 rounded-lg border" style={{ backgroundColor: settings.theme_primary }} />
                      <Input value={settings.theme_primary} onChange={e => updateField('theme_primary', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label>Couleur d'accent</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-10 h-10 rounded-lg border" style={{ backgroundColor: settings.theme_accent }} />
                      <Input value={settings.theme_accent} onChange={e => updateField('theme_accent', e.target.value)} />
                    </div>
                  </div>
                </div>
                <div><Label>Langue par défaut</Label><Input value={settings.default_language} onChange={e => updateField('default_language', e.target.value)} /></div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="securite" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Sécurité et accès</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="font-medium text-blue-800">Authentification</p>
                  <p className="text-sm text-blue-600 mt-1">Session basée sur cookies sécurisés (HttpOnly, SameSite)</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-800">Rôles disponibles</p>
                  <ul className="mt-2 space-y-1 text-sm text-slate-600">
                    <li>Administrateur - Accès complet</li>
                    <li>Formateur - Gestion des cours et sessions</li>
                    <li>Apprenant - Accès aux formations inscrites</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SettingsPage;
